import { Tooltip, TooltipContent, TooltipTrigger } from '@folio/components';
import { useInfiniteQuery, type useQueryClient } from '@tanstack/react-query';
import {
  type EmailThreadDto,
  emailThreadControllerGetThreadQueryKey,
  emailThreadControllerListThreads,
  emailThreadControllerListThreadsQueryKey,
  generalInboxControllerGetSummaryQueryKey,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Badge } from '@folio/components/ui/badge';

export function usePolicyEmailThreads({ policyId, search }: { policyId: string; search?: string }) {
  const params = { policyId, ...(search && { search }) };
  return useInfiniteQuery({
    queryKey: emailThreadControllerListThreadsQueryKey(params),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => emailThreadControllerListThreads({ ...params, cursor: pageParam }),
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    // Analysis relinks the thread's other emails after a manual change
    refetchInterval: query =>
      query.state.data?.pages.some(({ threads }) =>
        threads.some(({ analysisPending }) => analysisPending),
      )
        ? 1000
        : false,
  });
}

export const invalidateEmailQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  threadId?: string,
) => {
  queryClient.invalidateQueries({ queryKey: emailThreadControllerListThreadsQueryKey() });
  queryClient.invalidateQueries({ queryKey: generalInboxControllerGetSummaryQueryKey() });
  if (threadId) {
    queryClient.invalidateQueries({ queryKey: emailThreadControllerGetThreadQueryKey(threadId) });
  }
};

export const emailsToAttach = ({ emails = [] }: EmailThreadDto, policyId: string) =>
  emails.filter(
    ({ isArchived, linkedPolicies }) =>
      !isArchived && !linkedPolicies.some(({ id }) => id === policyId),
  );

// The thread's own flag only reads its latest email
export const hasAnyAttachment = ({ emails = [] }: EmailThreadDto) =>
  emails.some(({ hasAttachments }) => hasAttachments);

// ponytail: counts the loaded page only (50 threads); a server count when a policy outgrows it
export function PolicyEmailsTabLabel({ policyId }: { policyId: string }) {
  const { t } = useTranslation();
  const { data } = usePolicyEmailThreads({ policyId });
  const count =
    data?.pages
      .flatMap(({ threads }) => threads)
      .reduce((total, thread) => total + emailsToAttach(thread, policyId).length, 0) ?? 0;

  return (
    <span className="inline-flex items-center gap-1.5">
      {t('policyDashboardPages.exchanges')}
      {count > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="light"
              tone="warning"
              size="sm"
              className="tabular-nums"
              data-testid="policy-exchanges-tab-badge"
            >
              {count}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>{t('policyExchanges.toAttachTooltip', { count })}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}
